import { useState } from "react";
import { Search, Star, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@nexus/ui";

import {
  selectAssignableIndustries,
  sortIndicesByIndustryTitle,
} from "@/features/identity-hub/lib/assignable-industries";

import type { IndustryItem } from "@/features/identity-hub/api/identity-hub.api";

interface NewOrgIndustryRowsProps {
  industries: IndustryItem[];
  selected: string[];
  defaultIndex: string | null;
  onSelectedChange: (indices: string[]) => void;
  onDefaultChange: (index: string) => void;
}

function industryMonogram(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]![0] ?? ""}${words[1]![0] ?? ""}`.toUpperCase();
  }
  return title.slice(0, 2).toUpperCase();
}

/** Selected industries as rows, with a search row to add more (Wonder data-access step). */
export function NewOrgIndustryRows({
  industries,
  selected,
  defaultIndex,
  onSelectedChange,
  onDefaultChange,
}: NewOrgIndustryRowsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const byIndex = new Map(industries.map((industry) => [industry.index, industry]));
  const selectedItems = sortIndicesByIndustryTitle(
    selected,
    new Map([...byIndex].map(([index, industry]) => [index, industry.title])),
  ).flatMap((index) => {
    const industry = byIndex.get(index);
    return industry ? [industry] : [];
  });
  const remaining = selectAssignableIndustries(industries).filter(
    (industry) => !selected.includes(industry.index),
  );

  const add = (index: string) => {
    if (selected.includes(index)) return;
    onSelectedChange([...selected, index]);
    setOpen(false);
  };

  const remove = (index: string) => {
    onSelectedChange(selected.filter((current) => current !== index));
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {selectedItems.map((industry) => {
        const isDefault = industry.index === defaultIndex;
        return (
          <div
            key={industry.index}
            className={cn(
              "flex items-center gap-3 border-b border-border-subtle px-3 py-2.5",
              isDefault && "bg-primary-subtle",
            )}
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#eeeef4]">
              <span className="font-mono text-[10px] font-medium leading-none text-[#464b67]">
                {industryMonogram(industry.title)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium leading-snug text-foreground">
                {industry.title}
              </p>
              <p className="truncate font-mono text-[10px] leading-snug text-muted-foreground">
                {industry.index}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onDefaultChange(industry.index)}
              className={cn(
                "inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2 text-[11px] leading-none",
                isDefault
                  ? "border border-primary/30 bg-background font-medium text-primary-subtle-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Star className={cn("size-3", isDefault ? "fill-primary text-primary" : "text-[#9ca3af]")} />
              {isDefault
                ? t("iam.newOrgDialog.industries.defaultPill", "Default")
                : t("iam.newOrgDialog.industries.makeDefault", "Make default")}
            </button>
            <button
              type="button"
              aria-label={t("iam.newOrgDialog.industries.remove", "Remove {{name}}", {
                name: industry.title,
              })}
              onClick={() => remove(industry.index)}
              className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 w-full items-center gap-2.5 bg-muted px-3 text-left"
          >
            <Search className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="text-[13px] leading-none text-[#9ca3af]">
              {t(
                "iam.newOrgDialog.industries.addPlaceholder",
                "Add an industry — search {{count}} more…",
                { count: remaining.length },
              )}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={4} className="w-(--radix-popover-trigger-width) p-0">
          <Command>
            <CommandInput
              placeholder={t("iam.newOrgDialog.industries.search", "Search industries…")}
            />
            <CommandList>
              <CommandEmpty>
                {t("iam.newOrgDialog.industries.empty", "No industries found.")}
              </CommandEmpty>
              <CommandGroup>
                {remaining.map((industry) => (
                  <CommandItem
                    key={industry.index}
                    value={`${industry.title} ${industry.index}`}
                    onSelect={() => add(industry.index)}
                  >
                    <span className="flex-1 truncate">{industry.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
